import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import type { RuleTopic } from '../vacationHelp.types';

interface RulesCenterProps {
    isOpen: boolean;
    onClose: () => void;
    topics: RuleTopic[];
    defaultTopicId?: string;
    onTourClick?: () => void;
}

export function RulesCenter({ isOpen, onClose, topics, defaultTopicId, onTourClick }: RulesCenterProps) {
    const defaultIndex = topics.length > 0 ? Math.max(topics.findIndex(t => t.id === defaultTopicId), 0) : 0;
    const [currentIndex, setCurrentIndex] = useState(defaultIndex);

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(defaultIndex);
        }
    }, [isOpen, defaultIndex]);

    if (topics.length === 0) return null;

    const currentTopic = topics[currentIndex];

    const goNext = () => {
        if (currentIndex < topics.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const goPrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleClose = () => {
        setCurrentIndex(defaultIndex);
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto w-full max-w-4xl max-h-[85vh] rounded-xl bg-white shadow-lg overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="relative bg-gradient-to-r from-primary-700 to-primary-500 px-6 py-6 text-white flex items-center justify-between">
                        <div>
                            <Dialog.Title className="text-2xl font-bold font-montserrat">
                                Regras de Férias
                            </Dialog.Title>
                            <p className="mt-1 text-primary-100 text-sm font-montserrat">
                                Tudo que você precisa saber
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="rounded-full p-2 hover:bg-white/20 transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 h-full">
                            {/* Navigation - mobile as tabs, desktop as sidebar */}
                            <div className="lg:col-span-1 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-x-auto lg:overflow-y-auto">
                                <div className="flex lg:flex-col gap-0 p-3 lg:p-4 lg:divide-y">
                                    {topics.map((topic, idx) => (
                                        <button
                                            key={topic.id}
                                            onClick={() => setCurrentIndex(idx)}
                                            className={`flex-shrink-0 px-4 py-3 text-left rounded-lg transition-all whitespace-nowrap lg:whitespace-normal ${
                                                currentIndex === idx
                                                    ? 'bg-primary-600 text-white font-semibold'
                                                    : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            <span className="text-lg mr-2">{topic.icon}</span>
                                            {topic.title}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Topic Content */}
                            <div className="lg:col-span-3 p-6 lg:p-8">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentTopic.id}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-6"
                                    >
                                        {/* Icon and Title */}
                                        <div className="flex items-center gap-4">
                                            <div className="text-4xl">{currentTopic.icon}</div>
                                            <h2 className="text-2xl font-bold text-gray-900 font-montserrat">
                                                {currentTopic.title}
                                            </h2>
                                        </div>

                                        {/* Summary */}
                                        <p className="text-gray-700 text-sm leading-relaxed font-montserrat">
                                            {currentTopic.summary}
                                        </p>

                                        {/* Illustration */}
                                        {currentTopic.illustration && (
                                            <div className="bg-gray-50 rounded-lg p-6 flex justify-center">
                                                <currentTopic.illustration className="h-32 w-full" />
                                            </div>
                                        )}

                                        {/* Facts */}
                                        {currentTopic.facts && currentTopic.facts.length > 0 && (
                                            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                                                {currentTopic.facts.map((fact, i) => (
                                                    <div key={i} className="bg-primary-50 rounded-lg p-4 border border-primary-200">
                                                        <div className="text-2xl font-bold text-primary-600 font-montserrat">
                                                            {fact.value}
                                                        </div>
                                                        <div className="text-xs text-gray-600 mt-1 font-montserrat">
                                                            {fact.label}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Content */}
                                        <div className="prose prose-sm max-w-none text-gray-700">
                                            {currentTopic.content}
                                        </div>

                                        {/* Example */}
                                        {currentTopic.example && (
                                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                                                <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide font-montserrat">
                                                    💡 Exemplo
                                                </p>
                                                <p className="text-sm text-blue-800 mt-2 font-montserrat">
                                                    {currentTopic.example}
                                                </p>
                                            </div>
                                        )}

                                        {/* Callout */}
                                        {currentTopic.callout && (
                                            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                                                <p className="text-sm text-amber-800 font-montserrat">
                                                    ⚠️ {currentTopic.callout}
                                                </p>
                                            </div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
                        <button
                            onClick={onTourClick}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-2 font-montserrat"
                        >
                            🧭 Fazer o tour guiado
                        </button>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={goPrev}
                                disabled={currentIndex === 0}
                                className="rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-gray-200 transition-colors"
                            >
                                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                            </button>

                            <span className="text-xs text-gray-600 font-montserrat">
                                {currentIndex + 1} de {topics.length}
                            </span>

                            <button
                                onClick={goNext}
                                disabled={currentIndex === topics.length - 1}
                                className="rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed enabled:hover:bg-gray-200 transition-colors"
                            >
                                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
